import React from 'react';
import { DateTextInput } from './DateTextInput.jsx';
import { NumericField } from './NumberInput.jsx';
import { fmtMoney } from '../engine.js';
import { todayISO } from '../formatting.js';

const PILL_SM_BASE = 'inline-flex items-center text-[10.5px] px-[7px] py-px rounded-xl border font-[inherit] tracking-[0.04em] cursor-default';
const PILL_SM = `${PILL_SM_BASE} bg-surface-raised text-text-muted border-border`;
const PILL_SM_OK = `${PILL_SM_BASE} bg-[rgba(34,197,94,0.15)] text-accent-green border-[rgba(34,197,94,0.35)]`;
const BTN_GHOST_SM = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-2 py-[2px] rounded-sm font-[inherit] text-[11px] cursor-pointer self-start hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const BTN_GHOST = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-[0.7rem] py-[0.45rem] rounded-sm font-[inherit] text-[0.82rem] cursor-pointer self-start hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const BTN_ICON = 'bg-surface-raised text-text-muted border border-border rounded-sm w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:border-red-soft';
const REPAY_INPUT = 'bg-surface-raised text-text border border-border rounded-sm px-2 py-[0.35rem] font-[inherit] text-[0.82rem] w-full focus:outline-none focus:border-accent-blue';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';

export default function PiecewiseEditor({
  label, defaultMonthly, schedule, onChange, expectedTotalLabel,
}) {
  const arr = Array.isArray(schedule) ? schedule : [];
  const hasOverride = arr.length > 0;
  const sum = arr.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const update = (idx, patch) => {
    const next = arr.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const addRow = () => {
    const last = arr[arr.length - 1];
    const today = new Date();
    const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-15`;
    onChange([
      ...arr,
      { date: last?.date ?? ymd, amount: Number(defaultMonthly) || 0, account: last?.account ?? '' },
    ]);
  };
  const removeRow = (idx) => {
    const next = arr.filter((_, i) => i !== idx);
    onChange(next.length === 0 ? null : next);
  };
  const enable = () => {
    onChange([
      { date: todayISO(), amount: Number(defaultMonthly) || 0, account: '' },
    ]);
  };
  const clear = () => onChange(null);

  return (
    <div className="border border-border rounded-[8px] px-3 py-[10px] mb-[10px] bg-white/[0.015]">
      <div className="flex items-center justify-between gap-3 mb-[6px]">
        <div className="flex items-baseline gap-[10px] flex-wrap">
          <strong className="text-text">{label}</strong>
          <span className="text-[11px] text-text-muted">
            default {fmtMoney(defaultMonthly, { decimals: 2 })}/mo
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasOverride
            ? <span className={PILL_SM_OK}>piecewise · {fmtMoney(sum, { decimals: 2 })}</span>
            : <span className={PILL_SM}>using default</span>}
          {hasOverride
            ? <button className={BTN_GHOST_SM} onClick={clear}>Reset to default</button>
            : <button className={BTN_GHOST_SM} onClick={enable}>Enable piecewise</button>}
        </div>
      </div>
      {hasOverride && (
        <div className="flex flex-col gap-[6px] mt-2">
          {arr.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[145px_20px_120px_1fr_32px] gap-[0.45rem] items-center">
              <DateTextInput
                className={REPAY_INPUT}
                value={row.date || ''}
                onChange={(e) => update(idx, { date: e.target.value })}
              />
              <span className="text-text-muted text-center text-[0.85rem]">$</span>
              <NumericField
                className={`${REPAY_INPUT} text-right tabular-nums`}
                value={row.amount || 0}
                onChange={(v) => update(idx, { amount: v })}
                decimals={2}
              />
              <input
                type="text"
                className={REPAY_INPUT}
                value={row.account || ''}
                placeholder="Account / note"
                onChange={(e) => update(idx, { account: e.target.value })}
              />
              <button className={BTN_ICON} onClick={() => removeRow(idx)} aria-label="Remove">✕</button>
            </div>
          ))}
          <button className={BTN_GHOST} onClick={addRow}>+ Add payment</button>
          {expectedTotalLabel && (
            <p className={HINT}>Expected: {expectedTotalLabel} · current sum {fmtMoney(sum, { decimals: 2 })}</p>
          )}
        </div>
      )}
    </div>
  );
}
